import type { ThemeAccentId, ThemeId, ThemePackId } from "./types";
import { contrastInk, darkenHex, lightenHex, mixHex, normalizeHexColor } from "./themeColor";

export const THEME_PACK_IDS = ["original", "vista", "xp", "glassy", "ops-night", "ops-classic"] as const;

/** Retired packs still hydrate so older companies / snapshots keep a real theme. */
const LEGACY_THEME_PACK: Record<string, ThemePackId> = {
  classic: "original",
  older: "xp",
  newer: "glassy",
};

export const DEFAULT_THEME_PACK: ThemePackId = "ops-classic";
export const DEFAULT_THEME_ACCENT: ThemeAccentId = "#4a90c8";

const LEGACY_THEME_ACCENT: Record<string, ThemeAccentId> = {
  blue: "#4a90c8",
  gold: "#d4a017",
  green: "#2e8b57",
};

export type ThemePackOption = {
  id: ThemePackId;
  label: string;
  hint: string;
  preview: {
    chrome: string;
    rail: string;
    panel: string;
    ink: string;
  };
};

export type ThemeAccentOption = {
  id: ThemeAccentId;
  label: string;
};

/** Settings labels — colors/fonts/chrome only. Layout stays the same. */
export const THEME_PACKS: ThemePackOption[] = [
  {
    id: "original",
    label: "Original",
    hint: "Night desk — clear surfaces, soft depth, Segoe UI. Same spacing and controls as every other pack.",
    preview: { chrome: "#141820", rail: "#10141a", panel: "#1c2330", ink: "#eef2f6" },
  },
  {
    id: "vista",
    label: "Windows Vista",
    hint: "Aero-inspired gradients and glossy chrome. Teal/sky glass without losing table contrast.",
    preview: { chrome: "#1a6a8a", rail: "#0f3a50", panel: "#1e465f", ink: "#e8f4fa" },
  },
  {
    id: "xp",
    label: "Windows XP era",
    hint: "Polished Luna: clear borders, chunkier controls, blue / olive / silver — not muddy gray.",
    preview: { chrome: "#0054e3", rail: "#1e2f52", panel: "#e8ecf2", ink: "#1a2744" },
  },
  {
    id: "glassy",
    label: "Glassy",
    hint: "Modern glassmorphism: frosted panels, soft borders, contemporary spacing. Ops data stays readable.",
    preview: { chrome: "#161a22", rail: "#10141c", panel: "#1c222c", ink: "#eef2f7" },
  },
  {
    id: "ops-night",
    label: "Ops Night",
    hint: "Dark dispatch / ATC console. Deep backgrounds, high-contrast type, cyan and amber status.",
    preview: { chrome: "#0e141c", rail: "#0a1016", panel: "#121820", ink: "#e8f0f4" },
  },
  {
    id: "ops-classic",
    label: "Ops Classic",
    hint: "Dense day desk. Navy module bar, lime accent, tight tables. Switch to Day for the light panels.",
    preview: { chrome: "#1f4c80", rail: "#e4ebf4", panel: "#ffffff", ink: "#1f2124" },
  },
];

/** Curated brand palette — plus a custom color input in Settings. */
export const THEME_ACCENTS: ThemeAccentOption[] = [
  { id: "#4a90c8", label: "Northstar blue" },
  { id: "#0a3d6b", label: "Navy" },
  { id: "#0078d4", label: "Vista blue" },
  { id: "#0054e3", label: "Luna blue" },
  { id: "#7eb8da", label: "Sky" },
  { id: "#00a2b8", label: "Teal" },
  { id: "#3dd6f5", label: "Cyan" },
  { id: "#2e8b57", label: "Green" },
  { id: "#6d8b3c", label: "Olive" },
  { id: "#d4a017", label: "Gold" },
  { id: "#e8a317", label: "Amber" },
  { id: "#ff6b35", label: "Safety orange" },
  { id: "#c23b22", label: "Brick" },
  { id: "#d9534f", label: "Alert red" },
  { id: "#8b5cf6", label: "Violet" },
  { id: "#5b6b7a", label: "Slate" },
  { id: "#c0c7d1", label: "Silver" },
  { id: "#f2f6fa", label: "Ice" },
  { id: "#1a1c1e", label: "Charcoal" },
];

export const THEME_ACCENT_IDS = THEME_ACCENTS.map((accent) => accent.id);

export type DeskChrome = {
  theme: ThemeId;
  themePack: ThemePackId;
  themeAccent: ThemeAccentId;
};

export type AccentVars = {
  "--accent": string;
  "--accent-ink": string;
  "--accent-deep": string;
  "--accent-soft": string;
  "--caption": string;
  "--caption-ink": string;
  "--hi": string;
  "--nav-active": string;
  "--nav-active-ink": string;
  "--gold": string;
  "--chrome-stripe": string;
};

export function isThemePackId(raw: unknown): raw is ThemePackId {
  return (
    raw === "original" ||
    raw === "vista" ||
    raw === "xp" ||
    raw === "glassy" ||
    raw === "ops-night" ||
    raw === "ops-classic"
  );
}

export function isThemeAccentId(raw: unknown): raw is ThemeAccentId {
  return normalizeHexColor(raw) !== null || (typeof raw === "string" && raw in LEGACY_THEME_ACCENT);
}

export function hydrateThemeId(raw?: unknown): ThemeId {
  if (raw === "light") return "light";
  if (raw === "dark") return "dark";
  return "dark";
}

export function hydrateThemePack(raw?: unknown): ThemePackId {
  if (isThemePackId(raw)) return raw;
  if (typeof raw === "string" && raw in LEGACY_THEME_PACK) return LEGACY_THEME_PACK[raw];
  return DEFAULT_THEME_PACK;
}

export function hydrateThemeAccent(raw?: unknown): ThemeAccentId {
  if (typeof raw === "string" && raw in LEGACY_THEME_ACCENT) return LEGACY_THEME_ACCENT[raw];
  return normalizeHexColor(raw) ?? DEFAULT_THEME_ACCENT;
}

export function hydrateDeskChrome(
  raw?: Partial<{ theme: unknown; themePack: unknown; themeAccent: unknown }> | null,
): DeskChrome {
  return {
    theme: hydrateThemeId(raw?.theme),
    themePack: hydrateThemePack(raw?.themePack),
    themeAccent: hydrateThemeAccent(raw?.themeAccent),
  };
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

/**
 * Read desk chrome from a snapshot payload, career save, or company blob.
 * Desk is the source of truth — companion / mobile should apply this, not a
 * phone-only localStorage chrome pick.
 */
export function deskChromeFromUnknown(raw?: unknown): DeskChrome {
  if (!isRecord(raw)) return hydrateDeskChrome(null);
  const nested = isRecord(raw.deskChrome) ? raw.deskChrome : null;
  const company = isRecord(raw.company) ? raw.company : null;
  return hydrateDeskChrome({
    theme: nested?.theme ?? raw.theme ?? company?.theme,
    themePack: nested?.themePack ?? raw.themePack ?? company?.themePack,
    themeAccent: nested?.themeAccent ?? raw.themeAccent ?? company?.themeAccent,
  });
}

export function themePackLabel(id: ThemePackId): string {
  return THEME_PACKS.find((p) => p.id === id)?.label ?? THEME_PACKS[0].label;
}

export function themeAccentLabel(id: ThemeAccentId): string {
  const hex = hydrateThemeAccent(id);
  return THEME_ACCENTS.find((a) => a.id === hex)?.label ?? hex.toUpperCase();
}

/**
 * Ops Classic chrome is a fixed palette: the lime rule, navy captions, headings and
 * links do not follow the accent picker. The picked accent still drives focus rings
 * and accent highlights (`--accent*`).
 */
const OPS_CLASSIC_BRAND: Record<ThemeId, Omit<AccentVars, "--accent" | "--accent-ink" | "--accent-deep" | "--accent-soft">> = {
  light: {
    "--caption": "#215085",
    "--caption-ink": "#ffffff",
    "--hi": "#215085",
    "--nav-active": "#245c94",
    "--nav-active-ink": "#ffffff",
    "--gold": "#215085",
    "--chrome-stripe": "#97cf14",
  },
  dark: {
    "--caption": "#1f4c80",
    "--caption-ink": "#ffffff",
    "--hi": "#a9d4f5",
    "--nav-active": "#2a5f97",
    "--nav-active-ink": "#ffffff",
    "--gold": "#cfe0f2",
    "--chrome-stripe": "#9ccc1e",
  },
};

/** Derive caption / nav / highlight from the picked brand color. */
export function accentVars(accent: ThemeAccentId, theme: ThemeId, pack: ThemePackId): AccentVars {
  const hex = hydrateThemeAccent(accent);
  if (pack === "ops-classic") {
    return {
      "--accent": hex,
      "--accent-ink": contrastInk(hex),
      "--accent-deep": theme === "light" ? darkenHex(hex, 0.28) : darkenHex(hex, 0.38),
      "--accent-soft": theme === "light" ? mixHex(hex, "#1c2228", 0.18) : lightenHex(hex, 0.18),
      ...OPS_CLASSIC_BRAND[theme],
    };
  }
  const deep = theme === "light" ? darkenHex(hex, 0.28) : darkenHex(hex, 0.38);
  const soft = theme === "light" ? mixHex(hex, "#1c2228", 0.18) : lightenHex(hex, 0.18);
  const caption = relativeCaption(hex, theme);
  let navActive = hex;
  let navInk = contrastInk(hex);
  let brand = hex;
  // Original + default blue keeps the shipped mixed chrome (blue caption, gold chips / kickers).
  if (pack === "original" && hex === DEFAULT_THEME_ACCENT) {
    brand = theme === "light" ? "#8a5f00" : "#d4a017";
    navActive = brand;
    navInk = "#1c1f23";
  }
  return {
    "--accent": hex,
    "--accent-ink": contrastInk(hex),
    "--accent-deep": deep,
    "--accent-soft": soft,
    "--caption": caption,
    "--caption-ink": contrastInk(caption),
    "--hi": soft,
    "--nav-active": navActive,
    "--nav-active-ink": navInk,
    "--gold": brand,
    "--chrome-stripe": pack === "original" && hex === DEFAULT_THEME_ACCENT ? brand : hex,
  };
}

function relativeCaption(hex: string, theme: ThemeId): string {
  return theme === "light" ? darkenHex(hex, 0.22) : darkenHex(hex, 0.42);
}

export function applyAccentVars(
  style: { setProperty(name: string, value: string): void },
  accent: ThemeAccentId,
  theme: ThemeId,
  pack: ThemePackId,
): AccentVars {
  const vars = accentVars(accent, theme, pack);
  for (const [name, value] of Object.entries(vars)) {
    style.setProperty(name, value);
  }
  return vars;
}

/** Apply pack/accent before first paint (localStorage) and when Settings change. */
export function applyDeskChromeDataset(
  target: {
    dataset: DOMStringMap;
    style?: { setProperty(name: string, value: string): void };
  },
  raw?: Partial<{ theme: unknown; themePack: unknown; themeAccent: unknown }> | null,
): DeskChrome {
  const chrome = hydrateDeskChrome(raw);
  target.dataset.theme = chrome.theme;
  target.dataset.themePack = chrome.themePack;
  target.dataset.themeAccent = chrome.themeAccent.replace(/^#/, "");
  if (target.style) {
    applyAccentVars(target.style, chrome.themeAccent, chrome.theme, chrome.themePack);
  }
  return chrome;
}
